import React, { useState } from 'react';

const TagGapAnalysis = () => {
    const [keyword, setKeyword] = useState('');
    const [userTags, setUserTags] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [copiedTag, setCopiedTag] = useState(false);

    const handleAnalyze = async (e) => {
        e.preventDefault();
        if (!keyword.trim()) return;

        setLoading(true);
        setError('');
        setResult(null);

        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/analysis/tag-gap', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    keyword: keyword.trim(),
                    userTags: userTags.split(',').map(t => t.trim()).filter(Boolean)
                })
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to complete Tag Gap comparison.');
            }
            setResult(data.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const copyTagsToClipboard = () => {
        if (!result?.suggestedTagList) return;
        navigator.clipboard.writeText(result.suggestedTagList.join(', '));
        setCopiedTag(true);
        setTimeout(() => setCopiedTag(false), 2000);
    };

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem', color: '#f8fafc' }}>
            <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
                <div style={{ 
                    display: 'inline-block', 
                    padding: '0.4rem 1rem', 
                    background: 'rgba(236, 72, 153, 0.15)', 
                    border: '1px solid rgba(236, 72, 153, 0.3)', 
                    borderRadius: '20px', 
                    fontSize: '0.85rem', 
                    color: '#ec4899',
                    marginBottom: '0.8rem',
                    fontWeight: 600
                }}>
                    🏷️ Phase 2 Differentiation Feature (15 Credits)
                </div>
                <h1 style={{ 
                    fontSize: '2.5rem', 
                    fontWeight: 800, 
                    background: 'linear-gradient(135deg, #ec4899, #8b5cf6, #3b82f6)', 
                    WebkitBackgroundClip: 'text', 
                    WebkitTextFillColor: 'transparent',
                    marginBottom: '0.5rem'
                }}>
                    Tag Overlap & Keyword Gap Comparison
                </h1>
                <p style={{ color: '#94a3b8', fontSize: '1.05rem', maxWidth: '650px', margin: '0 auto' }}>
                    Compare your video tags with top ranking competitors to identify high-volume missing keywords and boost search relevance.
                </p>
            </div>

            <form onSubmit={handleAnalyze} style={{ 
                background: 'rgba(15, 23, 42, 0.75)', 
                backdropFilter: 'blur(16px)', 
                border: '1px solid rgba(255, 255, 255, 0.1)', 
                borderRadius: '16px', 
                padding: '1.5rem', 
                marginBottom: '2rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
            }}>
                <input
                    type="text"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="Target Search Keyword (e.g. Next.js 15 Tutorial, Best Laptops 2026)..."
                    style={{
                        background: 'rgba(30, 41, 59, 0.8)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: '10px',
                        padding: '0.85rem 1.2rem',
                        color: '#fff',
                        fontSize: '1rem',
                        outline: 'none'
                    }}
                />
                <textarea
                    value={userTags}
                    onChange={(e) => setUserTags(e.target.value)}
                    placeholder="Enter your current video tags (comma separated, optional)..."
                    rows="3"
                    style={{
                        background: 'rgba(30, 41, 59, 0.8)',
                        border: '1px solid rgba(255, 255, 255, 0.15)',
                        borderRadius: '10px',
                        padding: '0.85rem 1.2rem',
                        color: '#fff',
                        fontSize: '0.95rem',
                        outline: 'none',
                        resize: 'vertical'
                    }}
                />
                <button
                    type="submit"
                    disabled={loading}
                    style={{
                        background: 'linear-gradient(135deg, #ec4899, #d946ef)',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '0.85rem 2rem',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '1rem',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        boxShadow: '0 4px 14px rgba(236, 72, 153, 0.4)',
                        alignSelf: 'flex-end'
                    }}
                >
                    {loading ? 'Analyzing Tag Gap...' : 'Analyze Tag Gap (15 Credits)'}
                </button>
            </form>

            {error && (
                <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#fca5a5', padding: '1rem 1.5rem', borderRadius: '12px', marginBottom: '2rem' }}>
                    ❌ {error}
                </div>
            )}

            {result && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    {/* Header metrics */}
                    <div style={{
                        background: 'rgba(30, 41, 59, 0.7)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '16px',
                        padding: '1.5rem',
                        display: 'flex',
                        justify: 'space-around',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '1.5rem'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Tag Overlap Score</div>
                            <div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#f472b6' }}>{result.overlapPercentage}%</div>
                        </div>

                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Coverage Status</div>
                            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#38bdf8' }}>{result.tagCoverageGrade}</div>
                        </div>

                        <button
                            onClick={copyTagsToClipboard}
                            style={{
                                background: 'rgba(99, 102, 241, 0.2)',
                                border: '1px solid rgba(99, 102, 241, 0.4)',
                                borderRadius: '10px',
                                color: '#a5b4fc',
                                padding: '0.6rem 1.2rem',
                                fontWeight: 600,
                                cursor: 'pointer'
                            }}
                        >
                            {copiedTag ? '✅ Tags Copied!' : '📋 Copy Optimized Tag List'}
                        </button>
                    </div>

                    {/* Missing High Volume Tags */}
                    <div>
                        <h2 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: '1rem', color: '#f1f5f9' }}>
                            🚨 Missing High-Frequency Competitor Tags
                        </h2>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                            {result.missingHighVolumeTags.map((item, idx) => (
                                <div key={idx} style={{
                                    background: 'rgba(15, 23, 42, 0.7)',
                                    border: '1px solid rgba(236, 72, 153, 0.2)',
                                    borderRadius: '12px',
                                    padding: '1rem'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                                        <span style={{ fontWeight: 700, color: '#f8fafc' }}>"{item.tag}"</span>
                                        <span style={{ fontSize: '0.75rem', color: '#f472b6', fontWeight: 600 }}>{item.frequency}</span>
                                    </div>
                                    <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{item.recommendation}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TagGapAnalysis;
